
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Eye, EyeOff, Shield, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import SimpleCaptcha, { SimpleCaptchaRef } from '@/components/admin/SimpleCaptcha';
import IPAccessGuard from '@/components/admin/IPAccessGuard';
import SecurityWrapper from '@/components/admin/SecurityWrapper';
import SecureInput from '@/components/admin/SecureInput';
import { useAuth } from '@/hooks/useAuth';

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({ 
    email: '', 
    password: '' 
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const captchaRef = useRef<SimpleCaptchaRef>(null);
  const { user, isAdmin, loading, signIn } = useAuth();
  const navigate = useNavigate();

  // Security: Block login attempts after 3 failed attempts
  useEffect(() => {
    if (isBlocked && blockTimeRemaining > 0) {
      const timer = setInterval(() => {
        setBlockTimeRemaining(prev => {
          if (prev <= 1) {
            setIsBlocked(false);
            setFailedAttempts(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isBlocked, blockTimeRemaining]);

  // Enhanced redirect logic for authenticated admin users
  useEffect(() => {
    console.log('AdminLogin redirect check - loading:', loading, 'user:', user?.email, 'isAdmin:', isAdmin);
    
    // Only redirect if we're sure about auth state and user is admin
    if (!loading && user && isAdmin) {
      console.log('User is authenticated admin, redirecting to dashboard');
      // Small delay to ensure state is stable
      const timer = setTimeout(() => {
        navigate('/admin/dashboard', { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, isAdmin, loading, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isBlocked) {
      toast.error(`Too many failed attempts. Please wait ${blockTimeRemaining} seconds.`);
      return;
    }
    
    if (!captchaRef.current?.validate()) {
      toast.error('Invalid security check. Please try again.');
      captchaRef.current?.refresh();
      return;
    }

    if (!credentials.email || !credentials.password) {
      toast.error('Please enter both email and password');
      return;
    }

    setIsLoading(true);

    try {
      console.log('Attempting login...');
      const { error } = await signIn(credentials.email, credentials.password);
      
      if (error) {
        throw error;
      }
      
      console.log('Login successful');
      setFailedAttempts(0);
      toast.success('Login successful! Redirecting...');
      
      // Clear form
      setCredentials({ email: '', password: '' });
      
    } catch (err: any) {
      console.log('Login error:', err);
      
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);
      
      if (newFailedAttempts >= 3) {
        setIsBlocked(true);
        setBlockTimeRemaining(300); // 5 minutes block
        toast.error('Too many failed attempts. Account temporarily blocked for 5 minutes.');
      } else {
        toast.error(err.message || `Invalid credentials. ${3 - newFailedAttempts} attempts remaining.`);
      }
    } finally {
      setIsLoading(false);
      captchaRef.current?.refresh();
    }
  };

  if (loading) {
    return (
      <SecurityWrapper>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            <p className="text-blue-200 text-sm">Checking authentication...</p>
          </div>
        </div>
      </SecurityWrapper>
    );
  }

  // If user is already authenticated and admin, show loading while redirecting
  if (user && isAdmin) {
    return (
      <SecurityWrapper>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            <p className="text-blue-200 text-sm">Redirecting to dashboard...</p>
          </div>
        </div>
      </SecurityWrapper>
    );
  }

  return (
    <IPAccessGuard>
      <SecurityWrapper requireAdmin={false}>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <Card className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-slate-700 p-8 text-center">
                <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg border border-white/20">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Secure Admin Portal</h1>
                <p className="text-blue-100 text-sm">Enhanced security access to administration panel</p>
              </div>

              <div className="p-8">
                {isBlocked && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Account Temporarily Blocked</p>
                        <p className="text-xs text-red-600 mt-1">
                          Too many failed login attempts. Please wait {Math.floor(blockTimeRemaining / 60)}:{(blockTimeRemaining % 60).toString().padStart(2, '0')} before trying again.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleLogin} className="space-y-6">
                  <SecureInput
                    id="email"
                    label="Administrator Email"
                    value={credentials.email}
                    onChange={(value) => setCredentials(prev => ({ ...prev, email: value }))}
                    type="email"
                    placeholder="Enter your admin email"
                    required={true}
                    disabled={isLoading || isBlocked}
                    validateEmail={true}
                    preventSQLInjection={true}
                  />

                  <div className="space-y-2">
                    <div className="relative">
                      <SecureInput
                        id="password"
                        label="Password"
                        value={credentials.password}
                        onChange={(value) => setCredentials(prev => ({ ...prev, password: value }))}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        required={true}
                        disabled={isLoading || isBlocked}
                        preventSQLInjection={true}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-8 text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isLoading || isBlocked}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <SimpleCaptcha
                    ref={captchaRef}
                    onValidationChange={setIsCaptchaValid}
                    disabled={isLoading || isBlocked}
                  />

                  <Button
                    type="submit"
                    disabled={isLoading || isBlocked || !isCaptchaValid}
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-slate-700 hover:from-blue-700 hover:to-slate-800 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-3">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Authenticating...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-3">
                        <LogIn className="w-5 h-5" />
                        <span>Secure Access</span>
                      </div>
                    )}
                  </Button>
                </form>

                <div className="mt-8 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="flex items-start space-x-3">
                    <Shield className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">Security Notice</p>
                      <p className="text-xs text-slate-600 mt-1">
                      This page is reserved for authorized administrators.
Any unauthorized login attempts, including brute-force activities, will be detected, logged, and may result in access restrictions or legal action.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </SecurityWrapper>
    </IPAccessGuard>
  );
};

export default AdminLogin;
